import React, { useState, useEffect, useRef } from 'react';
import { LayoutGrid, Globe, Layers, MapPin } from 'lucide-react';

/**
 * PlatformSwitcher — waffle menu for switching between NorthAI products.
 *
 * Renders a small LayoutGrid icon button. On click, opens a 280px dropdown
 * listing all products. Styled dark (bg-gray-800) to match the sidebar.
 *
 * Props:
 *   currentUser   — user object (used to pass JWT when opening other products)
 *   activeProduct — 'geoagent' | 'lead_concierge' | 'territory' (highlights active row)
 *   onEnterGeoAgent — optional callback when GeoAgent row is clicked from within GeoAgent
 *                     (defaults to no-op since you're already there)
 *   iconOnly      — when true, renders just the icon trigger with no surrounding text.
 *                   Used by NavRail in collapsed mode. Default: false.
 */
const PlatformSwitcher = ({
  currentUser,
  activeProduct = 'geoagent',
  onEnterGeoAgent,
  iconOnly = false,
  onOpen,           // called when the dropdown is opened — used by NavRail to auto-expand
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const isAdmin = currentUser?.role === 'admin';
  const conciergeUrl =
    process.env.REACT_APP_CONCIERGE_URL || 'http://localhost:3002';

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const openLeadConcierge = () => {
    const jwt = localStorage.getItem('token');
    const dest = jwt
      ? `${conciergeUrl}?token=${encodeURIComponent(jwt)}`
      : conciergeUrl;
    window.location.href = dest;
    setOpen(false);
  };

  const handleGeoAgent = () => {
    if (onEnterGeoAgent) onEnterGeoAgent();
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Trigger button — 44×44px minimum touch target */}
      <button
        onClick={() => {
          const opening = !open;
          setOpen(opening);
          if (opening && onOpen) onOpen();
        }}
        title="Switch product"
        className={`
          flex items-center justify-center w-9 h-9 rounded-lg transition-colors
          ${open
            ? 'bg-gray-600 text-white'
            : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
          }
        `}
        aria-label="Platform switcher"
        aria-expanded={open}
      >
        <LayoutGrid className="w-4 h-4" />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute top-full left-0 mt-2 w-72 rounded-xl bg-gray-800 shadow-xl z-50 overflow-hidden border border-gray-700"
          role="menu"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-700">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              NorthAI Platform
            </span>
          </div>

          {/* GeoAgent row */}
          <SwitcherRow
            icon={<Globe className="w-4 h-4 text-blue-400" />}
            dotColor="bg-blue-500"
            label="GeoAgent"
            isActive={activeProduct === 'geoagent'}
            action={
              <button
                onClick={handleGeoAgent}
                className="text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors"
              >
                {activeProduct === 'geoagent' ? 'Here' : 'Open'}
              </button>
            }
          />

          {/* Lead Concierge row — admin only */}
          {isAdmin && (
            <SwitcherRow
              icon={<Layers className="w-4 h-4 text-purple-400" />}
              dotColor="bg-purple-500"
              label="Lead Concierge"
              isActive={activeProduct === 'lead_concierge'}
              action={
                <button
                  onClick={openLeadConcierge}
                  className="text-xs font-medium text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Open
                </button>
              }
            />
          )}

          {/* Territory Service row — disabled */}
          <SwitcherRow
            icon={<MapPin className="w-4 h-4 text-gray-500" />}
            dotColor="bg-gray-600"
            label="Territory Service"
            isActive={false}
            muted
            action={
              <span className="text-xs font-medium text-gray-500 bg-gray-700 px-1.5 py-0.5 rounded">
                Soon
              </span>
            }
          />
        </div>
      )}
    </div>
  );
};

/**
 * SwitcherRow — a single product row inside the dropdown.
 *
 * Props:
 *   icon      — React element
 *   dotColor  — Tailwind bg class for the 8px colored dot
 *   label     — product name string
 *   isActive  — bool: highlights this row
 *   muted     — bool: dims text (for disabled products)
 *   action    — React element (right-side button/badge)
 */
const SwitcherRow = ({ icon, dotColor, label, isActive, muted = false, action }) => (
  <div
    className={`
      flex items-center gap-3 px-4 py-3
      ${isActive ? 'bg-gray-700' : 'hover:bg-gray-750'}
      transition-colors
    `}
    role="menuitem"
  >
    {/* Colored dot */}
    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />

    {/* Icon + label */}
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {icon}
      <span
        className={`text-sm font-medium truncate ${
          muted ? 'text-gray-500' : isActive ? 'text-white' : 'text-gray-200'
        }`}
      >
        {label}
      </span>
    </div>

    {/* Right-side action */}
    <div className="flex-shrink-0">{action}</div>
  </div>
);

export default PlatformSwitcher;
